import { SearchOutlined } from "@ant-design/icons";
import { Col, Form, Input, message, Row, Select, Spin } from "antd";
import { CALL_API } from "common/API";
import { APPROVAL_FILTER_TYPES, STATUS_CODE } from "common/Constants";
import { includes } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import {
  getAllDepartmentList,
  getClientid,
  getMenuList,
  getUserInfo,
  getUserRoleId,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { getMenuPermission } from "utils/AccessUtils";
import {
  DateFilterForm,
  ENCASHMENT_STATUS,
  ENCASHMENT_STATUS_LIST,
  EncashmentStatusTag,
  formatDate,
  formatDays,
  getDateFilterRange,
  getStatusLabel,
  StyledFilterRow,
  StyledTable,
} from "./common";
import LeaveEncashmentRequestModal from "./LeaveEncashmentRequestModal";

function LeaveEncashmentApproval({
  ClientId,
  userInfo,
  userRoleId,
  menuList,
  AllDepartmentList,
}) {
  const [dateForm] = Form.useForm();
  const [encashmentList, setEncashmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusId, setStatusId] = useState(ENCASHMENT_STATUS.WAITING);
  const [departmentId, setDepartmentId] = useState("");
  const [search, setSearch] = useState("");
  const [selectedEncashment, setSelectedEncashment] = useState(null);
  const [CSVData, setCSVData] = useState([]);
  const csvLinkEl = useRef();
  const containerRef = useRef(null);

  // Approve / reject needs edit on the "leaveencashmentapproval" menu.
  const canApprove = !!getMenuPermission(
    menuList,
    userRoleId,
    "leaveencashmentapproval"
  )?.edit;

  const isChairmanOrCEO = userInfo?.higherOfficialsList?.some(
    (official) => official.role === "CEO" && official.email === userInfo.userid
  );
  const isHR = userInfo?.higherOfficialsList?.some(
    (official) => official.role === "HR" && official.email === userInfo.userid
  );

  const getEncashmentList = async () => {
    setLoading(true);
    const { code, encashments = [] } = await CALL_API(
      `leave-encashment-list/${ClientId}`,
      "post",
      {
        status_id: statusId,
        ...getDateFilterRange(dateForm),
        reportingmanager: isChairmanOrCEO || isHR ? "" : userInfo?.userid,
        baselocationid: isHR ? userInfo?.baseLocationId : "",
      }
    );
    setLoading(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      setEncashmentList(encashments);
    } else {
      setEncashmentList([]);
    }
  };

  useEffect(() => {
    if (ClientId) getEncashmentList();
  }, [ClientId, statusId, userRoleId]);

  const filteredList = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return encashmentList
      .filter((item) => !statusId || item?.status_id === statusId)
      .filter(
        (item) =>
          !departmentId || String(item?.department_id) === String(departmentId)
      )
      .filter(
        (item) =>
          !searchText ||
          item?.fullname?.toLowerCase().includes(searchText) ||
          item?.employeeid?.toLowerCase().includes(searchText)
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [encashmentList, statusId, departmentId, search]);

  const columns = [
    { title: "Name", dataIndex: "fullname", key: "fullname" },
    { title: "Employee ID", dataIndex: "employeeid", key: "employeeid" },
    { title: "Department", dataIndex: "department_name", key: "department_name" },
    { title: "Leave Type", dataIndex: "leave_category_name", key: "leave_category_name" },
    {
      title: "Requested",
      dataIndex: "number_of_days",
      key: "number_of_days",
      render: formatDays,
    },
    {
      title: "Available Balance",
      dataIndex: "available_balance",
      key: "available_balance",
      render: (_, record) =>
        formatDays(record?.current_balance ?? record?.available_balance),
    },
    {
      title: "Submitted",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDate,
    },
    {
      title: "Status",
      dataIndex: "status_id",
      key: "status_id",
      render: (status) => <EncashmentStatusTag statusId={status} />,
    },
  ];

  const CSVHeaders = [
    "Name",
    "Employee ID",
    "Department",
    "Leave Type",
    "Requested Days",
    "Available Balance",
    "Submitted",
    "Status",
    "Reason",
    "Decline Reason",
  ].map((label) => ({ label, key: label }));

  const prepareCSV = () => {
    if (!filteredList.length) {
      message.error("No data available to download.");
      return;
    }
    setCSVData(
      filteredList.map((item) => ({
        Name: item?.fullname,
        "Employee ID": item?.employeeid,
        Department: item?.department_name,
        "Leave Type": item?.leave_category_name,
        "Requested Days": item?.number_of_days,
        "Available Balance": item?.current_balance ?? item?.available_balance,
        Submitted: formatDate(item?.created_at),
        Status: getStatusLabel(item?.status_id),
        Reason: item?.reason || "",
        "Decline Reason": item?.decline_reason || "",
      }))
    );
    setTimeout(() => csvLinkEl.current.link.click());
  };

  return (
    <>
      <Row className="page-title" ref={containerRef}>
        <Col xs={24} lg={20}>
          <StyledFilterRow>
            <Input
              className="search"
              allowClear
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              value={statusId}
              style={{ width: 180 }}
              options={ENCASHMENT_STATUS_LIST}
              onChange={setStatusId}
              getPopupContainer={() => containerRef.current}
            />
            <DateFilterForm
              form={dateForm}
              name="leave_encashment_approval_date_form"
              durationOptions={APPROVAL_FILTER_TYPES}
              initialDurationType={4}
              onChange={getEncashmentList}
              containerRef={containerRef}
            />
            <Select
              value={departmentId}
              showSearch
              optionFilterProp="label"
              options={[
                { value: "", label: "All Departments" },
                ...(AllDepartmentList || []).map((dept) => ({
                  value: dept?.department_id,
                  label: dept?.department_name,
                })),
              ]}
              onChange={setDepartmentId}
              getPopupContainer={() => containerRef.current}
            />
          </StyledFilterRow>
        </Col>
        <Col xs={24} lg={4} className="align-right">
          <ButtonOutlined onClick={prepareCSV}>Export CSV</ButtonOutlined>
          <CSVLink
            headers={CSVHeaders}
            data={CSVData}
            filename={`leave-encashment-${getStatusLabel(statusId)}.csv`}
            ref={csvLinkEl}
          />
        </Col>
      </Row>
      <Spin spinning={loading}>
        <StyledTable
          dataSource={filteredList}
          columns={columns}
          rowKey="encashment_id"
          rowClassName={(_, index) =>
            `clickable-row ${index % 2 === 0 ? "" : "non-white"}`
          }
          onRow={(record) => ({ onClick: () => setSelectedEncashment(record) })}
          pagination={filteredList.length > 100 ? { pageSize: 100 } : false}
        />
      </Spin>
      <LeaveEncashmentRequestModal
        isModalVisible={!!selectedEncashment}
        closeModal={() => setSelectedEncashment(null)}
        encashment={selectedEncashment}
        ClientId={ClientId}
        canApprove={canApprove}
        onActionComplete={getEncashmentList}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  ClientId: getClientid(state),
  userInfo: getUserInfo(state),
  userRoleId: getUserRoleId(state),
  menuList: getMenuList(state),
  AllDepartmentList: getAllDepartmentList(state),
});

export default connect(mapStateToProps, null)(LeaveEncashmentApproval);
